import { ActivatedRoute, Params, Route } from '@angular/router';
import { RouterStateUrl } from '@app/core/router';
import { DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { RealSettingOrigin } from '@cco/model';

export interface Breadcrumb {
  path: any[];
  label?: string;
  label$?: Observable<string>;
  // eslint-disable-next-line @typescript-eslint/ban-types
  labelSelector?: MemoizedSelector<object, string | number, DefaultProjectorFn<string | number>>;

  /**
   * Do not manually populate this.
   * @See layout.component.ts
   */
  routerLink?: string | any[];
}

export interface AppRouteData {
  /**
   * Navigate to this relative path when the back button is activated
   */
  backPath?: any[];

  breadcrumbs?: Breadcrumb[];

  /**
   * When this route is activated, clear the selection based on the key (e.g., 'customerId')
   */
  clearSelectionKey?: string;

  /**
   * Navigated to by FlexRoutingGuard when the media changes to gt-sm
   */
  fullPath?: any[];

  /**
   * The semver of the minimum component versions to require before allowing navigation
   */
  requiredVersions?: {
    [componentName: string]: string;
  };

  /**
   * Navigated to by FlexRoutingGuard when the media changes to lt-md
   */
  mobilePath?: any[];

  /**
   * PrivilegeGuard requires all the privileges before allowing navigation
   */
  requiredPrivileges?: string[] | string;

  /**
   * The prefix to prepend to the route navigated to on item selection.
   * Used to open children items in a parent view.
   * E.g., from Customers > Customer > Projects
   *         to Project > Project
   */
  selectRoutingPrefix?: any[];

  /**
   * Specify origin to manage settings for: USER or COMPANY
   */
  settingsOrigin?: RealSettingOrigin;

  /**
   * Set to true when a component that would normally display in side-by-side panes
   * should be displayed in a single pane.
   */
  singlePane?: boolean;
}

export interface AppRoute extends Route {
  children?: AppRoute[];
  data?: AppRouteData;
}

export type AppRoutes = AppRoute[];

function getParameterTokens(pathSegment: string): string[] {
  return pathSegment.match(/(:[a-zA-Z]+)/g);
}

function getParameterName(parameterToken: string): string {
  return parameterToken.slice(1);
}

export function getAlternateRoute(
  routerState: RouterStateUrl,
  alternateRouteAccessor: (routeData: any) => any[],
): any[] {
  const commands = [];

  routerState.urlSegments.forEach((urlSegment, i) => {
    if (routerState.routeData[i] && alternateRouteAccessor(routerState.routeData[i])) {
      alternateRouteAccessor(routerState.routeData[i]).forEach((pathSegment) => {
        let segment = pathSegment;
        if (typeof segment === 'string') {
          const parameterTokens = getParameterTokens(segment);
          if (parameterTokens) {
            parameterTokens.forEach((parameterToken) => {
              segment = (segment as string).replace(
                parameterToken,
                routerState.flatParams[getParameterName(parameterToken)],
              );
            });
          }
        }
        commands.push(segment);
      });
    }

    urlSegment.forEach((urlSeg) => {
      if (!routerState.routeData[i] || !alternateRouteAccessor(routerState.routeData[i])) {
        commands.push(urlSeg.path);
      }
      if (Object.getOwnPropertyNames(urlSeg.parameters).length > 0) {
        commands.push(urlSeg.parameters);
      }
    });
  });

  return commands;
}

export function getMobileRoute(routerState: RouterStateUrl): any[] {
  return getAlternateRoute(routerState, routeData => routeData.mobilePath);
}

export function getFullRoute(routerState: RouterStateUrl): any[] {
  return getAlternateRoute(routerState, routeData => routeData.fullPath);
}

export function updateActivatedRouteParams(
  route: ActivatedRoute,
  params?: Params,
  merge = true,
): any[] {
  const descendantsPath = getDescendantsPath(route);

  if (params && Object.getOwnPropertyNames(params).length > 0) {
    if (merge) {
      return [{ ...route.snapshot.params, ...params }, ...descendantsPath];
    } else {
      return [params, ...descendantsPath];
    }
  } else {
    if (merge) {
      return [route.snapshot.params, ...descendantsPath];
    } else {
      return descendantsPath;
    }
  }
}

export function getDescendantsPath(route: ActivatedRoute): any[] {
  const descendantsPath = [];

  let child = route.snapshot.firstChild;
  while (child) {
    if (child.url) {
      child.url.forEach((urlSegment) => {
        if (urlSegment.path) {
          descendantsPath.push(urlSegment.path);
        }
        if (urlSegment.parameters && Object.getOwnPropertyNames(urlSegment.parameters).length > 0) {
          descendantsPath.push(urlSegment.parameters);
        }
      });
    }

    child = child.firstChild;
  }

  return descendantsPath;
}

export function routeParamsFromRoute(route: ActivatedRoute): Observable<Params[]> {
  return of(route.pathFromRoot.map(r => r.params));
}
